import { useEffect, useRef } from "react";

export function Elog({ lines }: { lines: GraphLine[] }) {
  const canvasRef = useRef<null | HTMLCanvasElement>(null);

  useEffect(() => {
    const c = canvasRef.current!;
    const ctx = c.getContext("2d");

    renderToCanvas(c, ctx!, lines);
  }, [lines])

  return <canvas ref={canvasRef} id="log" width="1640" height="260" style={{ display: "block", width: "820px", height: "130px" }} />
}

export type GraphLine = {
  state: "OFF" | "SB" | "D" | "ON" | "YM" | "PU";
  start: string;
}

function stateToFlatState(state: "OFF" | "SB" | "D" | "ON" | "YM" | "PU" | ""): "OFF" | "SB" | "D" | "ON" | "" {
  if (state === "OFF" || state === "PU") {
    return "OFF";
  }
  if (state === "SB") {
    return "SB";
  }
  if (state === "D") {
    return "D";
  }
  if (state === "ON" || state === "YM") {
    return "ON";
  }
  return "";
}


function renderToCanvas(c: HTMLCanvasElement, ctx: CanvasRenderingContext2D, data: GraphLine[]) {

  const total = {
    "OFF": 0,
    "SB": 0,
    "D": 0,
    "ON": 0,
    "": 0,
  };
  let prevState: "OFF" | "SB" | "D" | "ON" | "YM" | "PU" | "" = "";
  let prevTime = "00:00:00";
  for (let i = 0; i < data.length; i += 1) {
    const current = data[i];
    const totalPrev = toSeconds(current.start) - toSeconds(prevTime);
    total[stateToFlatState(prevState)] += totalPrev;

    prevTime = current.start;
    prevState = current.state;
  }


  const { width } = c;
  const height = c.height - 1;
  const startPoint = height / 7;

  const cubeWidth = width / 26;
  const cubeHeight = (height - startPoint) / 4;

  const chartStartX = cubeWidth;
  const chartEndX = width - cubeWidth;
  const chartStartY = startPoint;
  const chartEndY = height;

  ctx.clearRect(0, 0, c.width, c.height);

  for (let i = cubeWidth; i < width; i += cubeWidth) {
    ctx.beginPath();
    ctx.moveTo(i, startPoint);
    ctx.lineTo(i, height);
    ctx.stroke();

    if (i < width - cubeWidth) {
      for (let m = 0; m < 4; m += 1) {
        ctx.beginPath();
        ctx.moveTo(i + cubeWidth / 2, startPoint + cubeHeight * m + cubeHeight / 2);
        ctx.lineTo(i + cubeWidth / 2, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(i + cubeWidth / 4, startPoint + cubeHeight * m + cubeHeight / 4 * 3);
        ctx.lineTo(i + cubeWidth / 4, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(i + cubeWidth / 4 * 3, startPoint + cubeHeight * m + cubeHeight / 4 * 3);
        ctx.lineTo(i + cubeWidth / 4 * 3, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();
      }
    }
  }
  for (let i = 0; i < 5; i += 1) {
    const y = startPoint + cubeHeight * i;
    ctx.beginPath();
    ctx.moveTo(cubeWidth, y);
    ctx.lineTo(width - cubeWidth, y);
    ctx.stroke();
  }
  const fullDaySeconds = toSeconds("24:00:00")

  for (let i = 1; i < data.length; i += 1) {
    const startAsPcnt = toSeconds(data[i - 1].start) / fullDaySeconds;
    const start = chartStartX + startAsPcnt * (chartEndX - chartStartX);
    const endAsPcnt = toSeconds(data[i].start) / fullDaySeconds;
    const end = chartStartX + endAsPcnt * (chartEndX - chartStartX); // pcnt * width
    const previousY = getY(data[i - 1], chartStartY, cubeHeight);
    const y = getY(data[i], chartStartY, cubeHeight);

    const prelineWidth = ctx.lineWidth;
    const prestrokeStyle = ctx.strokeStyle;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#004080";

    // this is the horizontal line for the previous data item
    ctx.beginPath();
    if (data[i - 1].state === "PU" || data[i - 1].state === "YM") {
      ctx.setLineDash([15, 10]);
    }
    ctx.moveTo(start, previousY);
    ctx.lineTo(end, previousY);
    ctx.stroke();
    ctx.setLineDash([]);

    // we want to also connect lines
    if (previousY !== -1) {
      ctx.beginPath();
      ctx.moveTo(end, previousY);
      ctx.lineTo(end, y);
      ctx.stroke();
    }

    ctx.lineWidth = prelineWidth;
    ctx.strokeStyle = prestrokeStyle;
  }


  ctx.font = "18px helvetica";
  const pullup = 5;
  ctx.fillText("OFF", 15, 85);
  ctx.fillText("SB", 27, 140);
  ctx.fillText("D", 38, 195);
  ctx.fillText("ON", 24, 250);

  for (let i = 0; i < 25; i += 1) {
    let h = `${i % 12}`;
    let offset = h.length * 5;
    if (i === 0 || i === 24) {
      h = "M";
      offset = 6;
    } else if (i === 12) {
      h = "N";
      offset = 6;
    }
    h = `${h}`;
    ctx.fillText(h, cubeWidth + cubeWidth * i - offset, 26);
  }
  ctx.fillText(formatTimeForGraph(total.OFF), width - cubeWidth + 9, 85 - pullup);
  ctx.fillText(formatTimeForGraph(total.SB), width - cubeWidth + 9, 140 - pullup);
  ctx.fillText(formatTimeForGraph(total.D), width - cubeWidth + 9, 195 - pullup);
  ctx.fillText(formatTimeForGraph(total.ON), width - cubeWidth + 9, 250 - pullup);
}

function toSeconds(time: string): number {
  const parts = time.split(":");
  return Number(parts[2]) + 60 * Number(parts[1]) + 60 * 60 * Number(parts[0]);
}

function getY({ state }: GraphLine, chartStartY: number, cubeHeight: number) {
  if (state === "OFF" || state === "PU") {
    return chartStartY + cubeHeight / 2;
  }
  if (state === "SB") {
    return chartStartY + cubeHeight + cubeHeight / 2;
  }
  if (state === "D") {
    return chartStartY + 2 * cubeHeight + cubeHeight / 2;
  }
  if (state === "ON" || state === "YM") {
    return chartStartY + 3 * cubeHeight + cubeHeight / 2;
  }
  return 0;
}

function formatTimeForGraph(seconds: number): string {
  const formattedNumber = (seconds / 60 / 60).toFixed(2);

  // Split the number into integer and decimal parts
  const parts = formattedNumber.split('.');

  // If the integer part has only one digit, add a leading zero
  if (parts[0].length === 1) {
    parts[0] = `0${parts[0]}`;
  }

  // Return the formatted number
  return parts.join('.');
}
